/**
 * The task drawer's Links section (DD-052): direct links with a remove
 * button, then carried links (TD-096) with a muted "via <item>" and no
 * remove. A click follows the link: the drawer closes, then navigates.
 * "Collega" turns the section into the picker, inside the drawer.
 */

import React, { useState } from "react";
import { CloseIcon, PlusIcon, SpinnerIcon } from "@/components/icons";
import { useAppStore } from "@/core/store/useAppStore";
import { useAddTaskLinkMutation, useRemoveTaskLinkMutation } from "../hooks/useTasks";
import type { Task, TaskDetail, TaskLinkOption } from "../types";
import { TaskLinkPicker } from "./TaskLinkPicker";
import { linkEntity, taskLinkPath } from "./taskLinks";

interface TaskLinksSectionProps {
  task: Task;
  /** `GET /api/tasks/<id>`, undefined until it lands. */
  detail: TaskDetail | undefined;
  failed: boolean;
  onFollow: (path: string) => void;
}

const SECTION_LABEL = "text-[10px] font-semibold text-gray-500 uppercase tracking-[0.1em]";

export function TaskLinksSection({ task, detail, failed, onFollow }: TaskLinksSectionProps) {
  const addToast = useAppStore((state) => state.addToast);
  const addLink = useAddTaskLinkMutation(task.id);
  const removeLink = useRemoveTaskLinkMutation(task.id);
  const [picking, setPicking] = useState(false);

  // A failed link keeps the picker open with its search (spec, Error handling).
  const handlePick = (option: TaskLinkOption) =>
    addLink.mutate(
      { type: option.type, targetId: option.id },
      {
        onSuccess: () => setPicking(false),
        onError: () => addToast({ message: "Impossibile collegare l'elemento.", type: "error" }),
      }
    );

  const handleRemove = (linkId: string) =>
    removeLink.mutate(linkId, {
      onError: () => addToast({ message: "Impossibile scollegare l'elemento.", type: "error" }),
    });

  let content: React.ReactNode;
  if (!detail) {
    content = failed ? (
      <p className="text-sm text-red-600">Impossibile caricare i collegamenti.</p>
    ) : (
      <div className="flex justify-center py-3">
        <SpinnerIcon className="w-5 h-5 text-gray-400" />
      </div>
    );
  } else if (picking) {
    content = (
      <TaskLinkPicker
        clientId={task.clientId}
        links={detail.links}
        pending={addLink.isPending}
        onPick={handlePick}
        onCancel={() => setPicking(false)}
      />
    );
  } else if (detail.links.length === 0 && detail.carried.length === 0) {
    content = <p className="text-sm text-gray-400">Nessun collegamento.</p>;
  } else {
    content = (
      <ul className="divide-y divide-gray-100">
        {detail.links.map((link) => {
          const Icon = linkEntity(link.type, link.meta).icon;
          return (
            <li key={link.id} className="flex items-center gap-2 py-2">
              <Icon className="w-4 h-4 shrink-0 text-gray-400" />
              <button
                type="button"
                onClick={() => onFollow(taskLinkPath(link, task.clientId))}
                className="flex-1 min-w-0 truncate text-left text-sm text-gray-800 hover:text-blue-700"
              >
                {link.title}
              </button>
              <button
                type="button"
                onClick={() => handleRemove(link.id)}
                disabled={removeLink.isPending}
                aria-label={`Scollega ${link.title}`}
                className="shrink-0 p-1 rounded-md text-gray-400 hover:text-rose-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </li>
          );
        })}
        {detail.carried.map((link) => {
          const Icon = linkEntity(link.type, link.meta).icon;
          return (
            <li key={`${link.type}:${link.targetId}`} className="flex items-center gap-2 py-2">
              <Icon className="w-4 h-4 shrink-0 text-gray-300" />
              <button
                type="button"
                onClick={() => onFollow(taskLinkPath(link, task.clientId))}
                className="min-w-0 truncate text-left text-sm text-gray-600 hover:text-blue-700"
              >
                {link.title}
              </button>
              <span className="ml-auto shrink-0 max-w-[45%] truncate text-xs text-gray-400">
                via {link.via.title}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className={SECTION_LABEL}>Collegamenti</h3>
        {detail && !picking && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            <PlusIcon className="w-4 h-4" />
            Collega
          </button>
        )}
      </div>
      {content}
    </section>
  );
}
